import { Suspense, lazy } from "react";
import "@/styles/sbb.css";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { SiteProvider, useSite } from "@/context/SiteContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LeadDialog from "@/components/LeadDialog";
import InfoDialog from "@/components/InfoDialog";
import BillAuditModal from "@/components/BillAuditModal";
import RoiChat from "@/components/RoiChat";
import ScrollToTop from "@/components/ScrollToTop";
import ErrorBoundary from "@/components/ErrorBoundary";
import useLenis from "@/hooks/useLenis";
import Home from "@/pages/Home";
import Business from "@/pages/Business";
import Homeowners from "@/pages/Homeowners";
import RoofVisualizerPage from "@/pages/RoofVisualizerPage";
import Learn from "@/pages/Learn";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Unsubscribe from "@/pages/Unsubscribe";
import Terms from "@/pages/Terms";
import Accessibility from "@/pages/Accessibility";
import ServicePage from "@/pages/CoastalServicePage";
import ServicesIndex from "@/pages/ServicesIndex";
import CareersPage from "@/pages/CareersPage";
import GalleryPage from "@/pages/GalleryPage";
import FloridaPage from "@/pages/FloridaPage";
import "@/styles/florida.css";
import ProjectsPage from "@/pages/ProjectsPage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import AboutPage from "@/pages/AboutPage";
import CityPage from "@/pages/CityPage";
import BlogIndex from "@/pages/BlogIndex";
import BlogPost from "@/pages/BlogPost";
import LiveQuotePage from "@/pages/LiveQuotePage";
import AcademyPage from "@/pages/AcademyPage";
import AcademyInside from "@/pages/AcademyInside";
import AcademyRegister from "@/pages/AcademyRegister";
import AcademyDashboard from "@/pages/AcademyDashboard";
import AcademyBelt from "@/pages/AcademyBelt";
import AcademyLesson from "@/pages/AcademyLesson";
import AcademyFinal from "@/pages/AcademyFinal";
import { AcademyGlossary, AcademyResources } from "@/pages/AcademyExtras";
import { AcademyCapstone, AcademyVerify, AcademyCareers } from "@/pages/AcademyCareer";
import { AcademyCertificate, AcademyVerifyPage } from "@/pages/AcademyCertificates";
import AdminPage from "@/pages/AdminPage";
import NotFound from "@/pages/NotFound";

function GlobalAuditModal() {
  const { auditOpen, closeAudit, auditSnapshot, auditFiles } = useSite();
  return (
    <BillAuditModal
      open={auditOpen}
      onClose={closeAudit}
      snapshot={auditSnapshot}
      initialFiles={auditFiles}
      roofSqFt={auditSnapshot?.roofSqFt}
      monthlySpend={auditSnapshot?.monthlySpend}
      initialTaxRate={auditSnapshot?.taxRate}
    />
  );
}

function SiteLayout() {
  return (
    <>
      <Header />
      <main>
        <Outlet />
      </main>
      <Footer />
      <LeadDialog />
      <InfoDialog />
      <GlobalAuditModal />
      <RoiChat />
    </>
  );
}

export default function App() {
  useLenis();
  return (
    <ErrorBoundary>
      <SiteProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            <Route element={<SiteLayout />}>
              <Route index element={<Home />} />
              <Route path="/business" element={<Business />} />
              <Route path="/homeowners" element={<Homeowners />} />
              <Route path="/roof-visualizer" element={<RoofVisualizerPage />} />
              <Route path="/learn" element={<Learn />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/accessibility" element={<Accessibility />} />
              <Route path="/services/:slug" element={<ServicePage />} />
              <Route path="/services" element={<ServicesIndex />} />
              <Route path="/careers" element={<CareersPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/florida-commercial-solar/*" element={<FloridaPage />} />
              <Route path="/florida-commercial-solar" element={<FloridaPage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/projects/:slug" element={<ProjectDetailPage />} />
              <Route path="/solar/:city" element={<CityPage />} />
              <Route path="/blog" element={<BlogIndex />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/live-quote" element={<LiveQuotePage />} />
              <Route path="/academy" element={<AcademyPage />} />
              <Route path="/academy/inside" element={<AcademyInside />} />
              <Route path="/academy/register" element={<AcademyRegister />} />
              <Route path="/academy/login" element={<AcademyRegister />} />
              <Route path="/academy/dashboard" element={<AcademyDashboard />} />
              <Route path="/academy/belts/:belt" element={<AcademyBelt />} />
              <Route path="/academy/lessons/:id" element={<AcademyLesson />} />
              <Route path="/academy/glossary" element={<AcademyGlossary />} />
              <Route path="/academy/resources" element={<AcademyResources />} />
              <Route path="/academy/capstone" element={<AcademyCapstone />} />
              <Route path="/academy/final" element={<AcademyFinal />} />
              <Route path="/academy/careers" element={<AcademyCareers />} />
              <Route path="/academy/certificates/:token" element={<AcademyCertificate />} />
              <Route path="/academy/verify/:token" element={<AcademyVerifyPage />} />
              <Route path="/academy/verify" element={<AcademyVerify />} />
              <Route path="*" element={<NotFound />} />
            </Route>
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/leads/:leadId" element={<AdminPage />} />
          </Routes>
          <Toaster position="bottom-center" richColors />
        </BrowserRouter>
      </SiteProvider>
    </ErrorBoundary>
  );
}
